import axios from "axios";
import {
  makeAutoObservable,
  action,
  observable,
  computed,
  runInAction,
} from "mobx";
import { get, eq, some } from "lodash";
import { API_HOST, RequestState, defaultErrorMessage } from "../constants";
import { getSessionCookie, isValidEmail } from "../utils";
import { userStore } from "../stores";

export class SettingsController {
  updateSettingsMessage = "";
  updateSettingsRequestState = RequestState.IDLE;

  // Settings data
  settings = {
    email: "",
    firstName: "",
    lastName: "",
    gender: "",
    country: "",
    dateOfBirth: "",
    weight: "",
    height: "",
    allergies: [],
    favoriteFoods: [],
    subscriptionTierId: "",
    currentPassword: "",
    newPassword: "",
  };

  _userStore = userStore;

  parseDelimitedList = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (!value || typeof value !== "string") return [];
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  };

  constructor() {
    makeAutoObservable(this, {
      updateSettingsMessage: observable,
      updateSettingsRequestState: observable,
      settings: observable,
      updateSettings: action,
      loadUserSettings: action,
      resetSettings: action,
      getUpdateSettingsMessage: computed,
      getUpdateSettingsRequestState: computed,
    });

    this.loadUserSettings();
  }

  loadUserSettings = async () => {
    if (!this._userStore.userObj) {
      try {
        await this._userStore.fetchUser();
      } catch (e) {
        return;
      }
    }

    if (!this._userStore.userObj) return;

    const rawDob = get(this._userStore.userObj, "dob", "");
    const normalizedDob =
      typeof rawDob === "string" && rawDob.includes("T")
        ? rawDob.split("T")[0]
        : rawDob || "";

    runInAction(() => {
      this.settings = {
        email: get(this._userStore.userObj, "email", ""),
        firstName: get(this._userStore.userObj, "firstName", ""),
        lastName: get(this._userStore.userObj, "lastName", ""),
        gender: get(this._userStore.userObj, "gender", ""),
        country: get(this._userStore.userObj, "country", ""),
        dateOfBirth: normalizedDob,
        weight: get(this._userStore.userObj, "weight", ""),
        height: get(this._userStore.userObj, "height", ""),
        allergies: this.parseDelimitedList(
          get(this._userStore.userObj, "allergies", "")
        ),
        favoriteFoods: this.parseDelimitedList(
          get(this._userStore.userObj, "favoriteFoods", "")
        ),
        subscriptionTierId: get(this._userStore.userObj, "subscriptionTierId", ""),
        currentPassword: "",
        newPassword: "",
      };
    });
  };

  updateSetting = (key, value) => {
    runInAction(() => {
      if (key.includes(".")) {
        const keys = key.split(".");
        let obj = this.settings;
        for (let i = 0; i < keys.length - 1; i++) {
          obj = obj[keys[i]];
        }
        obj[keys[keys.length - 1]] = value;
      } else {
        this.settings[key] = value;
      }
    });
  };

  updateSettings = async (_successCallback) => {
    const { email, firstName, lastName, currentPassword, newPassword } =
      this.settings;

    if (some([!email.length, !firstName.length, !lastName.length])) {
      runInAction(() => {
        this.updateSettingsMessage =
          "Please fill in required fields (email, first name, last name).";
      });
      return;
    }

    if (!isValidEmail(email)) {
      runInAction(() => {
        this.updateSettingsMessage = "Please enter a valid email address";
      });
      return;
    }

    if (newPassword && !currentPassword) {
      runInAction(() => {
        this.updateSettingsMessage =
          "Please enter your current password to set a new password.";
      });
      return;
    }

    runInAction(() => {
      this.updateSettingsRequestState = RequestState.REQUESTED;
      this.updateSettingsMessage = "";
    });

    try {
      const cookie = await getSessionCookie();
      const favoriteFoodsValue = Array.isArray(this.settings.favoriteFoods)
        ? this.settings.favoriteFoods.join(",")
        : this.settings.favoriteFoods;
      const allergiesValue = Array.isArray(this.settings.allergies)
        ? this.settings.allergies.join(",")
        : this.settings.allergies;

      const payload = {
        email: this.settings.email,
        firstName: this.settings.firstName,
        lastName: this.settings.lastName,
        gender: this.settings.gender,
        country: this.settings.country,
        dob: this.settings.dateOfBirth,
        weight: this.settings.weight,
        height: this.settings.height,
        allergies: allergiesValue,
        favoriteFoods: favoriteFoodsValue,
        subscriptionTierId: this.settings.subscriptionTierId,
        password: currentPassword,
        newPassword,
      };

      const response = await axios.put(`${API_HOST}user/update`, payload, {
        headers: {
          Authorization: cookie,
        },
      });

      if (eq(response.status, 200)) {
        runInAction(() => {
          this.updateSettingsMessage = "Settings updated successfully!";
        });

        // Update user store with new data
        await this._userStore.fetchUser();

        if (_successCallback) {
          _successCallback();
        }
      } else {
        runInAction(() => {
          this.updateSettingsMessage = get(
            response,
            "data.message",
            defaultErrorMessage
          );
        });
      }
    } catch (error) {
      console.log("Error updating settings", error);

      runInAction(() => {
        this.updateSettingsMessage = get(
          error,
          "response.data.error",
          defaultErrorMessage
        );
      });
    }

    runInAction(() => {
      this.updateSettingsRequestState = RequestState.RECEIVED;
    });
  };

  resetSettings = () => {
    this.loadUserSettings();
    runInAction(() => {
      this.updateSettingsMessage = "";
      this.updateSettingsRequestState = RequestState.IDLE;
    });
  };

  get getUpdateSettingsMessage() {
    return this.updateSettingsMessage;
  }

  get getUpdateSettingsRequestState() {
    return this.updateSettingsRequestState;
  }
}

export default SettingsController;
